/**
 * Eric 2026-05-12 — Page « Lacunes du Chatbot » (admin).
 * Liste les questions où le RAG n'a pas trouvé de contexte, groupées par
 * similarité. Permet de transformer les échecs du chatbot en pipeline de
 * contenu (demande de Q&R, ou dismiss).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  Robot,
  Question,
  CheckCircle,
  XCircle,
  Users,
  CircleNotch,
  ClipboardText,
  Sparkle,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

function StatusBadge({ status, lang }) {
  const map = {
    open: { cls: "bg-amber-100 text-amber-800", label: lang === "fr" ? "À traiter" : "Open" },
    qa_requested: { cls: "bg-blue-100 text-blue-800", label: lang === "fr" ? "Q&R demandée" : "Q&A requested" },
    dismissed: { cls: "bg-zinc-200 text-zinc-600", label: lang === "fr" ? "Ignorée" : "Dismissed" },
  };
  const item = map[status] || map.open;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${item.cls}`} data-testid={`gap-status-${status}`}>{item.label}</span>;
}

export default function ChatbotGapsPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("open");
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/chatbot-gaps`, { withCredentials: true });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestQA = async (gap) => {
    const ok = await confirmDialog(lang === "fr"
      ? `Demander à un expert de créer un Q&R pour : « ${gap.sample_question.slice(0, 80)} » ?`
      : `Request expert to create a Q&A for: "${gap.sample_question.slice(0, 80)}"?`);
    if (!ok) return;
    setProcessing(gap.normalized);
    try {
      await axios.post(`${API}/api/admin/chatbot-gaps/request-qa`, { query_ids: gap.query_ids }, { withCredentials: true });
      toast.success(lang === "fr" ? "Demande de Q&R enregistrée ✓" : "Q&A request saved ✓");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setProcessing(null);
  };

  const dismissGap = async (gap) => {
    const ok = await confirmDialog(lang === "fr" ? "Ignorer cette lacune ?" : "Dismiss this gap?");
    if (!ok) return;
    setProcessing(gap.normalized);
    try {
      await axios.post(`${API}/api/admin/chatbot-gaps/dismiss`, { query_ids: gap.query_ids }, { withCredentials: true });
      toast.success(lang === "fr" ? "Lacune ignorée" : "Gap dismissed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setProcessing(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12 text-zinc-500" data-testid="gaps-loading">
      <CircleNotch size={28} className="animate-spin mr-2" />
      {lang === "fr" ? "Chargement des lacunes…" : "Loading gaps…"}
    </div>;
  }
  if (error) return <div className="p-6 text-red-600 text-sm" data-testid="gaps-error">{error}</div>;

  const filtered = (data.gaps || []).filter(g => filterStatus === "all" || g.status === filterStatus);
  const counts = {
    open: data.gaps.filter(g => g.status === "open").length,
    qa_requested: data.gaps.filter(g => g.status === "qa_requested").length,
    dismissed: data.gaps.filter(g => g.status === "dismissed").length,
    all: data.gaps.length,
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="chatbot-gaps-page">
      <header className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 inline-flex items-center gap-2">
          <Robot size={28} weight="bold" className="text-amber-600" />
          {lang === "fr" ? "Lacunes du Chatbot" : "Chatbot Gaps"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? "Questions où le chatbot n'a pas trouvé de réponse — opportunités pour enrichir le catalogue."
            : "Questions where the chatbot found no answer — opportunities to enrich the catalog."}
        </p>
      </header>

      {/* Header stats */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/40 p-3" data-testid="stat-total-30d">
          <p className="text-[11px] uppercase font-semibold text-amber-800/70">{lang === "fr" ? "Sans contexte (30j)" : "No-context (30d)"}</p>
          <p className="text-2xl font-bold text-amber-900">{data.total_30d}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-3" data-testid="stat-unique-gaps">
          <p className="text-[11px] uppercase font-semibold text-zinc-500">{lang === "fr" ? "Lacunes uniques" : "Unique gaps"}</p>
          <p className="text-2xl font-bold text-zinc-900">{data.unique_gaps}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3" data-testid="stat-qa-requested">
          <p className="text-[11px] uppercase font-semibold text-blue-700/70">{lang === "fr" ? "Q&R demandées" : "Q&A requested"}</p>
          <p className="text-2xl font-bold text-blue-900">{counts.qa_requested}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3" data-testid="stat-dismissed">
          <p className="text-[11px] uppercase font-semibold text-zinc-500">{lang === "fr" ? "Ignorées" : "Dismissed"}</p>
          <p className="text-2xl font-bold text-zinc-700">{counts.dismissed}</p>
        </div>
      </section>

      {/* Tabs filter */}
      <div className="flex items-center gap-1 border-b border-zinc-200 mb-4">
        {[
          { v: "open", l: lang === "fr" ? "À traiter" : "Open" },
          { v: "qa_requested", l: lang === "fr" ? "Q&R demandées" : "Q&A requested" },
          { v: "dismissed", l: lang === "fr" ? "Ignorées" : "Dismissed" },
          { v: "all", l: lang === "fr" ? "Toutes" : "All" },
        ].map(t => (
          <button
            key={t.v}
            onClick={() => setFilterStatus(t.v)}
            className={`relative px-3 py-2 text-sm font-medium ${filterStatus === t.v ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"}`}
            data-testid={`gaps-filter-${t.v}`}
          >
            {t.l} <span className="text-[10px] text-zinc-400">({counts[t.v]})</span>
            {filterStatus === t.v && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-400 text-sm" data-testid="gaps-empty">
          <Sparkle size={36} className="mx-auto mb-2 opacity-30" />
          {filterStatus === "open"
            ? (lang === "fr" ? "Aucune lacune à traiter 🎉 — votre catalogue couvre toutes les questions récentes." : "No open gaps 🎉 — your catalog covers all recent questions.")
            : (lang === "fr" ? "Aucune lacune dans cette vue." : "No gaps in this view.")}
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map(gap => (
            <li key={gap.normalized} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`gap-item-${gap.normalized.slice(0, 20)}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <StatusBadge status={gap.status} lang={lang} />
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500">
                      <Question size={11} weight="bold" />
                      {gap.count} {lang === "fr" ? "fois" : "times"}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500">
                      <Users size={11} weight="bold" />
                      {gap.users_count} {lang === "fr" ? "personne(s)" : "user(s)"}
                    </span>
                    {gap.last_asked && (
                      <span className="text-[10px] text-zinc-400">
                        {lang === "fr" ? "dernière" : "last"}: {new Date(gap.last_asked).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-900 font-medium leading-snug">« {gap.sample_question} »</p>
                </div>
                {gap.status === "open" && (
                  <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                    <button
                      onClick={() => requestQA(gap)}
                      disabled={processing === gap.normalized}
                      className="text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white hover:bg-[#001F7A] disabled:opacity-40 inline-flex items-center gap-1.5"
                      data-testid="gap-request-qa-btn"
                    >
                      {processing === gap.normalized ? <CircleNotch size={11} className="animate-spin" /> : <ClipboardText size={11} weight="bold" />}
                      {lang === "fr" ? "Demander Q&R" : "Request Q&A"}
                    </button>
                    <button
                      onClick={() => dismissGap(gap)}
                      disabled={processing === gap.normalized}
                      className="text-[11px] font-semibold px-3 py-1.5 rounded border border-zinc-300 text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 inline-flex items-center gap-1.5"
                      data-testid="gap-dismiss-btn"
                    >
                      <XCircle size={11} weight="bold" />
                      {lang === "fr" ? "Ignorer" : "Dismiss"}
                    </button>
                  </div>
                )}
                {gap.status === "qa_requested" && (
                  <button
                    onClick={() => navigate("/dashboard/admin/review")}
                    className="text-[11px] font-semibold px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1.5 shrink-0"
                    data-testid="gap-open-review-btn"
                  >
                    <ArrowSquareOut size={11} weight="bold" />
                    {lang === "fr" ? "Ouvrir revue" : "Open review"}
                  </button>
                )}
                {gap.status === "dismissed" && (
                  <CheckCircle size={16} weight="bold" className="text-zinc-300 shrink-0 mt-1" />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
